import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Trash2, FileText, CheckCircle } from "lucide-react";
import { FileUpload } from "../components/FileUpload";
import type { UploadBatch, UploadResponse } from "../types";
import { bankLabels, formatSEK } from "../lib/format";

interface UploadPageProps {
  batches: UploadBatch[];
  onUploadComplete: (responses: UploadResponse[]) => void;
  onDeleteBatch: (id: string) => void;
}

export function UploadPage({
  batches,
  onUploadComplete,
  onDeleteBatch,
}: UploadPageProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /**
   * Fetched rather than linked so a missing file surfaces as an inline message
   * instead of navigating the SPA away to a JSON error page.
   */
  async function handleDownload(batch: UploadBatch) {
    setDownloadingId(batch.id);
    setDownloadError(null);

    try {
      const response = await fetch(`/api/batches/${batch.id}/file`);

      if (!response.ok) {
        setDownloadError(
          response.status === 404
            ? `Originalfilen för ${batch.fileName} finns inte kvar på servern.`
            : `Kunde inte ladda ner ${batch.fileName}.`
        );
        return;
      }

      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = batch.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(`Kunde inte ladda ner ${batch.fileName}.`);
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Ladda upp kontoutdrag</CardTitle>
          <CardDescription>
            Släpp in kontoutdrag, kvitton, fakturor eller exportfiler. Appen läser CSV, Excel, PDF, text/JSON och använder AI som fallback när bankformatet inte känns igen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FileUpload onUploadComplete={onUploadComplete} />
        </CardContent>
      </Card>

      {batches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Uppladdade filer</CardTitle>
            <CardDescription>
              Ladda ner originalfilen som den såg ut när du laddade upp den.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {downloadError && (
              <div className="mb-3 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {downloadError}
              </div>
            )}
            <div className="space-y-3">
              {batches.map((batch) => (
                <div
                  key={batch.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">{batch.fileName}</p>
                      <p className="text-xs text-muted-foreground">
                        {bankLabels[batch.bank] ?? batch.bank} &middot;{" "}
                        {batch.transactionCount} transaktioner &middot;{" "}
                        {new Date(batch.uploadedAt).toLocaleDateString("sv-SE")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      disabled={downloadingId === batch.id}
                      onClick={() => void handleDownload(batch)}
                      title={`Ladda ner ${batch.fileName}`}
                      aria-label={`Ladda ner ${batch.fileName}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => onDeleteBatch(batch.id)}
                      title={`Ta bort ${batch.fileName}`}
                      aria-label={`Ta bort ${batch.fileName}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
